import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import "./Projects.css";
import portfolioBlessingImage from "../../assets/images/portfolio-blessing.png";
import gmCodingImage from "../../assets/images/gm-coding.png";
import gitForgeImage from "../../assets/images/Git-forge.png";
import gmcImage from "../../assets/images/gmc.png"


const Projects: React.FC = () => {
  const [activeProjectIndex, setActiveProjectIndex] = useState(0);
  const touchStartXRef = useRef<number | null>(null);
  const touchEndXRef = useRef<number | null>(null);
  const swipeDetectedRef = useRef(false);

  const projects = [
    {
      id: 1,
      title: "Portfolio Website",
      description:
        "Modern, responsive portfolio website showcasing creative work with smooth animations and interactive elements.",
      image: portfolioBlessingImage,
      technologies: [],
      liveUrl: "https://blessingb.vercel.app/",
    },
    {
      id: 2,
      title: "GM Coding",
      description:
        "A technical education platform built around structured mentorship, Java, C++, DSA, and practical problem-solving.",
      image: gmCodingImage,
      technologies: [],
      liveUrl: "https://gm-coding-two.vercel.app/",
    },
    {
      id: 3,
      title: "GitForge",
      description:
        "A compact Git learning and productivity app for commits, command discovery, and branching workflow clarity.",
      image: gitForgeImage,
      technologies: [],
      liveUrl: "https://ows-git-forge.vercel.app/",
    },
    {
      id: 4,
      title: "GMC Moving Services",
      description:
        "A service website for home, office, and business relocations, designed for clear requests and confident conversion.",
      image: gmcImage,
      technologies: [],
      liveUrl: "https://gmclog.vercel.app/",
    },
  ];
  const featuredProject = projects[activeProjectIndex];

  const goToNextProject = () => {
    setActiveProjectIndex((current) => (current + 1) % projects.length);
  };

  const goToPreviousProject = () => {
    setActiveProjectIndex((current) =>
      (current - 1 + projects.length) % projects.length,
    );
  };

  const handleGalleryTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    swipeDetectedRef.current = false;
    touchStartXRef.current = touch.clientX;
    touchEndXRef.current = touch.clientX;
  };

  const handleGalleryTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    touchEndXRef.current = touch.clientX;
  };

  const resetTouchTracking = () => {
    touchStartXRef.current = null;
    touchEndXRef.current = null;
  };

  const handleGalleryTouchEnd = () => {
    if (touchStartXRef.current === null || touchEndXRef.current === null) {
      resetTouchTracking();
      return;
    }

    const deltaX = touchStartXRef.current - touchEndXRef.current;
    const minimumSwipeDistance = 50;

    if (Math.abs(deltaX) > minimumSwipeDistance) {
      swipeDetectedRef.current = true;
      if (deltaX > 0) goToNextProject();
      else goToPreviousProject();
    }

    resetTouchTracking();
  };

  const getGalleryPosition = (index: number) => {
    let position = index - activeProjectIndex;

    if (position > projects.length / 2) position -= projects.length;
    if (position < -projects.length / 2) position += projects.length;

    return position;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
      },
    },
  };

  return (
    <section id="projects-gallery" className="projects">
      <div className="container">
        <motion.div
          className="projects-content"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          <motion.div
            className="project-gallery"
            variants={cardVariants}
            aria-label="Project gallery"
            onTouchStart={handleGalleryTouchStart}
            onTouchMove={handleGalleryTouchMove}
            onTouchEnd={handleGalleryTouchEnd}
            onTouchCancel={resetTouchTracking}
          >
            {projects.map((project, index) => (
              <motion.button
                key={project.id}
                type="button"
                className={`gallery-project gallery-project--${getGalleryPosition(
                  index,
                )}${index === activeProjectIndex ? " active" : ""}`}
                onClick={(event) => {
                  if (swipeDetectedRef.current) {
                    swipeDetectedRef.current = false;
                    event.preventDefault();
                    return;
                  }

                  if (index !== activeProjectIndex) {
                    setActiveProjectIndex(index);
                  }
                }}
                onFocus={() => setActiveProjectIndex(index)}
                aria-pressed={index === activeProjectIndex}
                aria-label={`Open ${project.title}`}
              >
                <img src={project.image} alt={project.title} />
              </motion.button>
            ))}
          </motion.div>

          <motion.div className="featured-project" variants={cardVariants}>
            <h3>{featuredProject.title}</h3>
            <p>{featuredProject.description}</p>
            <a
              href={featuredProject.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="featured-link"
            >
              View Project
              <span aria-hidden="true">→</span>
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
